import NeptuneElement from '../../NeptuneElement.js';
import { i18nMixin } from '../../../utils/i18nMixin.js';
import { i18n } from '../../../utils/i18n.js';

class Snackbar extends i18nMixin(NeptuneElement) {

    static AUTO_DISMISS_DELAY_MS = 5000;
    static SNACKBAR_OPEN_STATE = ['true', 'false'];

    static ICON_MAP = {
        info: 'fa-solid fa-circle-info',
        success: 'fa-solid fa-circle-check',
        warning: 'fa-solid fa-circle-exclamation',
        error: 'fa-solid fa-circle-xmark'
    };

    templateMarkup = `
        <div role="status" aria-live="polite" aria-atomic="true" id="snackbar">
            <i></i>
            <span class="font-size-md">
                <slot></slot>
            </span>
            <neptune-button type="tertiary" size="small" aria-label="Action"></neptune-button>
            <neptune-button type="tertiary" size="small" icon-end="fa-solid fa-xmark" aria-label="Dismiss notification"></neptune-button>
        </div>
    `;

    componentCssUrl = '/src/components/core/snackbar/snackbar.css';

    #snackbarElement = null;
    #actionButtonElement = null;
    #closeButtonElement = null;
    #iconElement = null;
    #autoDismissTimerId = null;
    #handleActionButtonClickBound = null;
    #handleCloseButtonClickBound = null;

    constructor() {
        super();
    }

    async connectedCallback() {
        await super.connectedCallback();
        const culture = this.getAttribute('culture');
        if (culture) {
            i18n.setLocale(culture);
        }
        this.#applyTranslations();
        this.setEventListeners();
        this.processInitialAttributesAfterRender(Snackbar.observedAttributes);
    }

    async disconnectedCallback() {
        this.#clearAutoDismissTimer();
        await super.disconnectedCallback();
    }

    static get observedAttributes() {
        return ['open','type','action-label', 'culture'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    processAttribute(name, oldValue, newValue) {
        if (oldValue === newValue) {
            return;
        }
        switch (name) {
            case 'culture':
                if (newValue) {
                    i18n.setLocale(newValue);
                    this.onLocaleChange();
                }
                break;
            case 'open':
                if (newValue !== null && newValue !== '') {
                    this.validatePropertyValue(name, newValue, Snackbar.SNACKBAR_OPEN_STATE);
                }
                this.#processOpenAttribute(newValue);
                break;
            case 'type':
                if (newValue !== null) {
                    this.validatePropertyValue(name, newValue, Object.keys(Snackbar.ICON_MAP));
                }
                this.#processTypeAttribute(newValue);
                break;
            case 'action-label':
                this.#processActionLabelAttribute(newValue);
                break;
            default:
                break;
        }
    }

    setEventListeners() {
        this.#snackbarElement = this.shadowRoot?.querySelector('div');
        this.#actionButtonElement = this.shadowRoot?.querySelector('div > neptune-button:first-of-type');
        this.#closeButtonElement = this.shadowRoot?.querySelector('div > neptune-button:last-of-type');
        this.#iconElement = this.shadowRoot?.querySelector('div > i');

        this.#handleActionButtonClickBound = this.#handleActionButtonClick.bind(this);
        this.#handleCloseButtonClickBound = this.#handleCloseButtonClick.bind(this);

        this.#actionButtonElement?.addEventListener('click', this.#handleActionButtonClickBound);
        this.#closeButtonElement?.addEventListener('click', this.#handleCloseButtonClickBound);
        this.#processTypeAttribute(this.getAttribute('type'));
        this.#processActionLabelAttribute(this.getAttribute('action-label'));
        this.#applyTranslations();
    }

    /**
     * @description Applies translations to button aria-labels when locale changes.
     * Updates the action button and close button aria-labels with translated text.
     */
    onLocaleChange() {
        if (this.isRendered) {
            this.#applyTranslations();
        }
    }

    /**
     * @description Applies translations to the action and close button aria-labels.
     * Sets aria-label based on whether action-label attribute is set.
     */
    #applyTranslations() {
        if (!this.#actionButtonElement || !this.#closeButtonElement) {
            return;
        }
        
        const actionLabel = this.getAttribute('action-label');
        const hasActionLabel = Boolean(actionLabel?.trim());
        
        if (hasActionLabel) {
            this.#actionButtonElement.setAttribute('aria-label', actionLabel);
        } else {
            this.#actionButtonElement.setAttribute('aria-label', this.translate('SNACKBAR.ACTION'));
        }
        
        this.#closeButtonElement.setAttribute('aria-label', this.translate('SNACKBAR.DISMISS'));
    }

    removeEventListeners() {
        this.#actionButtonElement?.removeEventListener('click', this.#handleActionButtonClickBound);
        this.#closeButtonElement?.removeEventListener('click', this.#handleCloseButtonClickBound);
        this.#snackbarElement = null;
        this.#actionButtonElement = null;
        this.#closeButtonElement = null;
        this.#iconElement = null;
        this.#handleActionButtonClickBound = null;
        this.#handleCloseButtonClickBound = null;
    }

    open() {
        this.setAttribute('open', 'true');
    }

    close() {
        this.setAttribute('open', 'false');
    }

    #processOpenAttribute(openValue) {
        if (this.#isOpenValue(openValue)) {
            this.#showSnackbar();
        } else {
            this.#hideSnackbar();
        }
    }

    #processTypeAttribute(typevalue){
        if (!this.#iconElement) {return;}
        Object.values(this.constructor.ICON_MAP).forEach(classElement=> {
            this.#iconElement.classList.remove(...classElement.split(' '));
        });
        if (typevalue && this.constructor.ICON_MAP[typevalue]) {
            this.#iconElement.classList.add(...this.constructor.ICON_MAP[typevalue].split(' '));
            this.#iconElement.style.display = '';
            this.#iconElement.setAttribute('aria-label', typevalue);
        } else {
            this.#iconElement.style.display = 'none';
            this.#iconElement.removeAttribute('aria-label');
        }
    }

    #processActionLabelAttribute(actionLabelValue) {
        if (!this.#actionButtonElement) {return;}

        const hasActionLabel = Boolean(actionLabelValue?.trim());
        this.#actionButtonElement.textContent = actionLabelValue ?? '';
        this.#actionButtonElement.style.display = hasActionLabel ? '' : 'none';
        const ariaLabel = hasActionLabel ? actionLabelValue : this.translate('SNACKBAR.ACTION');
        this.#actionButtonElement.setAttribute('aria-label', ariaLabel);
    }

    #isOpenValue(value) {
        return value === '' || value === 'true';
    }

    #showSnackbar() {
        if (!this.#snackbarElement) {return;}
        this.#snackbarElement.setAttribute('data-visible', 'true');
        this.#startAutoDismissTimer();
    }

    #hideSnackbar() {
        if (!this.#snackbarElement) {return;}
        this.#snackbarElement.removeAttribute('data-visible');
        this.#clearAutoDismissTimer();
    }

    #startAutoDismissTimer() {
        this.#clearAutoDismissTimer();
        this.#autoDismissTimerId = setTimeout(() => {
            this.close();
        }, Snackbar.AUTO_DISMISS_DELAY_MS);
    }

    #clearAutoDismissTimer() {
        if (this.#autoDismissTimerId === null) {return;}
        clearTimeout(this.#autoDismissTimerId);
        this.#autoDismissTimerId = null;
    }

    #handleCloseButtonClick() {
        this.close();
    }

    #handleActionButtonClick() {
        this.dispatchEvent(new CustomEvent('snackbar-action', {
            bubbles: true,
            composed: true,
            detail: {
                actionLabel: this.getAttribute('action-label')
            }
        }));
    }
}

customElements.define('neptune-snackbar', Snackbar);
export default Snackbar;
